import { useMemo, useState } from 'react'
import { FileSpreadsheet } from 'lucide-react'
import type { Account, Dataset, Property } from '@/types/entities'
import { SectionCard, EmptyState, Chip, Badge, KpiTile, KpiGrid } from '@/components/ui'
import { PnLTable } from '@/components/finance/PnLTable'
import { noi, sumGroup, financialPeriodsForProperty, propertyById } from '@/lib/selectors'
import { money, deltaStr, pct } from '@/lib/format'
import type { AccountDerived } from '../lib'

export default function FinancialsTab({ d, data }: { account: Account; d: AccountDerived; data: Dataset }) {
  // Properties on this account that actually have a financial period.
  const propsWithFp = useMemo(
    () => d.properties.filter((p) => financialPeriodsForProperty(data, p.id).length > 0),
    [d.properties, data],
  )

  const [selId, setSelId] = useState<string | undefined>(propsWithFp[0]?.id)

  if (!d.financialPeriods.length || !propsWithFp.length) {
    return (
      <SectionCard pad>
        <EmptyState
          icon={<FileSpreadsheet size={36} />}
          title="P&L not loaded for this account"
          hint="Property-level P&L is sourced from the Yardi GL. It has not been loaded for this account in the prototype — only the JPMorgan JV carries a full 2026-Q1 financial package."
        />
      </SectionCard>
    )
  }

  const selProp: Property | undefined = selId ? propertyById(data, selId) : propsWithFp[0]
  const fp = selProp ? financialPeriodsForProperty(data, selProp.id)[0] : undefined

  const revA = fp ? sumGroup(fp, 'revenue', 'actual') : 0
  const revB = fp ? sumGroup(fp, 'revenue', 'budget') : 0
  const noiA = fp ? noi(fp, 'actual') : 0
  const noiB = fp ? noi(fp, 'budget') : 0
  const noiVar = noiA - noiB
  const noiMargin = revA ? (noiA / revA) * 100 : undefined

  return (
    <div className="aw-stack">
      <SectionCard
        eyebrow="Financials"
        title={<>Property <b>P&L</b></>}
        right={fp && <Badge tone="navy" variant="outline">{fp.period} · {fp.source ?? 'Yardi GL'}</Badge>}
      >
        <div className="aw-chip-row aw-chip-row--wrap">
          <span className="label aw-chip-row-label">Property</span>
          {propsWithFp.map((p) => (
            <Chip key={p.id} active={p.id === (selProp?.id)} onClick={() => setSelId(p.id)}>
              {p.externalIds?.lcode ?? p.name}
            </Chip>
          ))}
        </div>

        {selProp && (
          <div className="aw-fin-head">
            <h3 className="aw-fin-title">{selProp.name}</h3>
            <span className="aw-cell-sub">{selProp.address?.city}, {selProp.address?.state}</span>
          </div>
        )}

        <KpiGrid cols={4}>
          <KpiTile label="Revenue (Actual)" value={money(revA, { compact: true })} sub={`Budget ${money(revB, { compact: true })}`} />
          <KpiTile
            label="NOI (Actual)" value={money(noiA, { compact: true })} accent
            sub={`Budget ${money(noiB, { compact: true })}`}
          />
          <KpiTile label="NOI Variance" value={deltaStr(noiVar, 'money').replace(/\.00$/, '')} sub={noiB ? `${noiVar >= 0 ? '+' : ''}${((noiVar / Math.abs(noiB)) * 100).toFixed(1)}% vs budget` : undefined} />
          <KpiTile label="NOI Margin" value={pct(noiMargin)} />
        </KpiGrid>

        <div className={`aw-variance-callout aw-variance-callout--${noiVar >= 0 ? 'ok' : 'alert'}`}>
          {noiVar >= 0
            ? <>NOI is running <b>{deltaStr(noiVar, 'money')}</b> ahead of budget this period{revA ? <> on a {pct(noiMargin)} margin</> : null}.</>
            : <>NOI is <b>{deltaStr(noiVar, 'money')}</b> behind budget this period — review the controllable-expense lines below.</>}
        </div>
      </SectionCard>

      {fp && (
        <SectionCard eyebrow={fp.period} title={<>Actual-to-Budget <b>P&L</b></>} pad>
          <PnLTable fp={fp} />
        </SectionCard>
      )}
    </div>
  )
}
